// Shared helpers for for-sale listing display and filtering.
// Used by both the single-property For Sale tab and its filter panel.

export function fmt(cents) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function isSaturdayUnit(unitNumber) {
  return /[AB]$/i.test(unitNumber || '');
}

// Core Driftwood interval-calendar math: week 1 starts on the first
// occurrence of the given start day on or after Jan 1.
function weekDateRange(weekNumber, year, isSaturday) {
  const startDay = isSaturday ? 6 : 5; // 6=Sat, 5=Fri
  const jan1  = new Date(Date.UTC(year, 0, 1));
  const dow   = jan1.getUTCDay();
  const diff  = (startDay - dow + 7) % 7;
  const w1    = new Date(jan1);
  w1.setUTCDate(w1.getUTCDate() + diff);
  const checkIn  = new Date(w1);
  checkIn.setUTCDate(w1.getUTCDate() + (weekNumber - 1) * 7);
  const checkOut = new Date(checkIn);
  checkOut.setUTCDate(checkIn.getUTCDate() + 7);
  return { checkIn, checkOut };
}

// Per-unit week dates (A/B buildings start Saturday, everything else Friday).
export function getDriftwoodWeekRange(unitNumber, weekNumber, year) {
  return weekDateRange(weekNumber, year, isSaturdayUnit(unitNumber));
}

export function getDriftwoodWeekDates(unitNumber, weekNumber, year) {
  const { checkIn, checkOut } = getDriftwoodWeekRange(unitNumber, weekNumber, year);
  const fmtDate = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${fmtDate(checkIn)} – ${fmtDate(checkOut)}`;
}

// Buckets a for-sale listing's room_type into the fixed bedroom-count filter options.
export function bedroomCategory(roomType) {
  const rt = (roomType || '').toLowerCase();
  if (rt.includes('2 bedroom')) return '2 Bedroom';
  if (rt.includes('1 bedroom')) return '1 Bedroom';
  if (rt.includes('studio') || rt.includes('efficiency')) return 'Studio';
  return null;
}

// for_sale_listings.view_type is blank for most rows — fall back to the
// per-unit room_type_descriptions record, which has it consistently filled in.
export function effectiveViewType(listing, descriptionsByUnit = {}) {
  return listing.view_type || descriptionsByUnit[listing.unit_number]?.view_type || '';
}

// ── Week-range filter helpers ────────────────────────────────────────────────
// The filter UI works in week-number space (matching for_sale_listings.week_number
// directly), using the majority Friday-start calendar as the canonical reference
// for display — individual listing rows still show their own building's exact
// dates via getDriftwoodWeekDates above.

export function formatWeekRange(weekNumber, year) {
  if (!weekNumber || weekNumber < 1 || weekNumber > 53) return '';
  const { checkIn, checkOut } = weekDateRange(weekNumber, year, false);
  const fmtDate = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${fmtDate(checkIn)} – ${fmtDate(checkOut)}`;
}

// "Week 27 — Jul 3 to Jul 10", used as dropdown option text.
export function formatWeekOptionLabel(weekNumber, year) {
  if (!weekNumber || weekNumber < 1 || weekNumber > 53) return '';
  const { checkIn, checkOut } = weekDateRange(weekNumber, year, false);
  const fmtDate = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `Week ${weekNumber} — ${fmtDate(checkIn)} to ${fmtDate(checkOut)}`;
}

// Canonical (Friday-start) week bounds as ISO date strings — used to turn a
// week-number selection into check_in_after/check_in_before query params.
export function canonicalWeekDateRange(weekNumber, year) {
  const { checkIn, checkOut } = weekDateRange(weekNumber, year, false);
  return { checkIn: checkIn.toISOString().slice(0, 10), checkOut: checkOut.toISOString().slice(0, 10) };
}

export function weekMonthName(weekNumber, year) {
  if (!weekNumber || weekNumber < 1 || weekNumber > 53) return '';
  const { checkIn } = weekDateRange(weekNumber, year, false);
  return checkIn.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
}

// Inverse lookup: which canonical (Friday-start) week number contains this date.
// Rolls back to the previous year's week numbering for early-January dates that
// fall before that year's week 1 start.
export function weekNumberForDate(dateStr) {
  const date = new Date(`${dateStr}T00:00:00Z`);
  let year = date.getUTCFullYear();
  let { checkIn: w1 } = weekDateRange(1, year, false);
  if (date < w1) {
    year -= 1;
    ({ checkIn: w1 } = weekDateRange(1, year, false));
  }
  const diffDays = Math.floor((date - w1) / 86400000);
  return { week: Math.floor(diffDays / 7) + 1, year };
}

function ordinal(n) {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
}

export function shortDateOrdinal(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const mon = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${mon} ${ordinal(d.getUTCDate())}`;
}

export function formatFullDate(dateStr) {
  return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
